import React from 'react';
import { Settings, Plus, Edit2, Trash2, CheckSquare, ArrowRight, List, MessageSquare, Loader } from 'lucide-react';
import { G1, G2, GE, GG, INP, SS, PERM_KEYS } from '../../config/styles';
import { contentSave, contentDelete } from '../../config/adminApi';
import CustomersManager from './CustomersManager';
import StaffManager from './StaffManager';

// ── مدير الجانبين ────────────────────────────────────
function SidebarManager() {
  const [playlists,    setPlaylists]    = React.useState([]);
  const [items,        setItems]        = React.useState({});
  const [activeTab,    setActiveTab]    = React.useState('right');
  const [editPl,       setEditPl]       = React.useState(null);
  const [showPlForm,   setShowPlForm]   = React.useState(false);
  const [showItemForm, setShowItemForm] = React.useState(null); // playlist_id
  const [plForm,       setPlForm]       = React.useState({ name_ar:'', name_en:'', icon:'🎬', order_num:0 });
  const [itemForm,     setItemForm]     = React.useState({ type:'video', title_ar:'', content:'', description_ar:'', link_url:'', pay_url:'', duration:'', order_num:0 });
  const [editingItem,  setEditingItem]  = React.useState(null); // id العنصر اللي بنعدّله
  const [waStd,        setWaStd]        = React.useState(''); // لينك واتساب الستاندر من الإعدادات

  React.useEffect(() => {
    (async () => {
      const { supabase } = await import('../../config/supabase');
      const { data } = await supabase.from('settings').select('whatsapp_number, whatsapp_message').limit(1);
      const st = data && data[0];
      if (st && st.whatsapp_number) {
        const num = String(st.whatsapp_number).replace(/[^0-9]/g, '');
        const msg = st.whatsapp_message ? ('?text=' + encodeURIComponent(st.whatsapp_message)) : '';
        setWaStd('https://wa.me/' + num + msg);
      }
    })();
  }, []);
  const [saving,       setSaving]       = React.useState(false);

  const loadPlaylists = async () => {
    const { supabase } = await import('../../config/supabase');
    const { data } = await supabase.from('sidebar_playlists').select('*').eq('side', activeTab).order('order_num');
    if (data) setPlaylists(data);
  };

  const loadItems = async (plId) => {
    const { supabase } = await import('../../config/supabase');
    const { data } = await supabase.from('sidebar_items').select('*').eq('playlist_id', plId).order('order_num');
    if (data) setItems(p => ({ ...p, [plId]: data }));
  };

  React.useEffect(() => { loadPlaylists(); }, [activeTab]);

  const savePl = async () => {
    setSaving(true);
    const row = editPl
      ? { ...plForm, side: activeTab, id: editPl.id }
      : { ...plForm, side: activeTab };
    const r = await contentSave('sidebar_playlists', row);
    setSaving(false);
    if (r.error) { alert(r.error); return; }
    setShowPlForm(false); setEditPl(null);
    setPlForm({ name_ar:'', name_en:'', icon:'🎬', order_num:0 });
    loadPlaylists();
  };

  const deletePl = async (id) => {
    if (!window.confirm('حذف القائمة وكل عناصرها؟')) return;
    const r = await contentDelete('sidebar_playlists', id);
    if (r.error) { alert(r.error); return; }
    loadPlaylists();
  };

  const togglePl = async (pl) => {
    const r = await contentSave('sidebar_playlists', { id: pl.id, is_active: !pl.is_active });
    if (r.error) { alert(r.error); return; }
    loadPlaylists();
  };

  const saveItem = async (plId) => {
    setSaving(true);
    const row = editingItem
      ? { ...itemForm, id: editingItem }
      : { ...itemForm, playlist_id: plId };
    const r = await contentSave('sidebar_items', row);
    setSaving(false);
    if (r.error) { alert(r.error); return; }
    setShowItemForm(null); setEditingItem(null);
    setItemForm({ type:'video', title_ar:'', content:'', description_ar:'', link_url:'', pay_url:'', duration:'', order_num:0 });
    loadItems(plId);
  };

  // فتح الفورم لتعديل عنصر موجود (بيملاه بقيمه)
  const openEditItem = (item, plId) => {
    setEditingItem(item.id);
    setItemForm({
      type: item.type || 'video',
      title_ar: item.title_ar || '',
      content: item.content || '',
      description_ar: item.description_ar || '',
      link_url: item.link_url || '',
      pay_url: item.pay_url || '',
      duration: item.duration || '',
      order_num: item.order_num || 0,
    });
    setShowItemForm(plId);
  };

  // فتح فورم إضافة جديد (يفضّي القيم)
  const openAddItem = (plId) => {
    setEditingItem(null);
    setItemForm({ type:'video', title_ar:'', content:'', description_ar:'', link_url:'', pay_url:'', duration:'', order_num:0 });
    setShowItemForm(showItemForm===plId ? null : plId);
  };

  const deleteItem = async (id, plId) => {
    const r = await contentDelete('sidebar_items', id);
    if (r.error) { alert(r.error); return; }
    loadItems(plId);
  };

  const INP_S = { padding:'6px 10px', border:'1px solid #e5e7eb', borderRadius:'6px', fontSize:'12px', width:'100%', boxSizing:'border-box', fontFamily:'Cairo, sans-serif', outline:'none' };

  return (
    <div style={{ marginBottom:'28px' }}>
      <h3 style={{ fontSize:'15px', fontWeight:'bold', color:'#1F2937', marginBottom:'12px' }}>📱 مدير الجانبين (Sidebar)</h3>

      {/* تاب يمين/شمال */}
      <div style={{ display:'flex', gap:'8px', marginBottom:'14px' }}>
        {[['right','📌 الجانب الأيمن'],['left','📌 الجانب الأيسر']].map(([v,l]) => (
          <button key={v} onClick={() => setActiveTab(v)}
            style={{ padding:'7px 18px', border:`2px solid ${activeTab===v?'#5a67d8':'#e5e7eb'}`, borderRadius:'8px', background:activeTab===v?'#eef2ff':'#fff', color:activeTab===v?'#5a67d8':'#374151', fontWeight:activeTab===v?'bold':'400', cursor:'pointer', fontSize:'13px', fontFamily:'Cairo, sans-serif' }}>
            {l}
          </button>
        ))}
        <button onClick={() => { setShowPlForm(true); setEditPl(null); setPlForm({ name_ar:'', name_en:'', icon:'🎬', order_num:playlists.length }); }}
          style={{ padding:'7px 14px', background:'linear-gradient(135deg,#5a67d8,#764ba2)', color:'#fff', border:'none', borderRadius:'8px', fontSize:'12px', fontWeight:'bold', cursor:'pointer', marginRight:'auto', fontFamily:'Cairo, sans-serif' }}>
          + إضافة قائمة
        </button>
      </div>

      {/* فورم القائمة */}
      {showPlForm && (
        <div style={{ background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:'10px', padding:'14px', marginBottom:'12px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 60px 60px', gap:'8px', marginBottom:'8px' }}>
            <input value={plForm.name_ar} onChange={e => setPlForm(p => ({...p, name_ar:e.target.value}))} placeholder="اسم القائمة عربي" style={INP_S} />
            <input value={plForm.name_en} onChange={e => setPlForm(p => ({...p, name_en:e.target.value}))} placeholder="English name" dir="ltr" style={INP_S} />
            <input value={plForm.icon}    onChange={e => setPlForm(p => ({...p, icon:e.target.value}))}    placeholder="🎬" style={{ ...INP_S, textAlign:'center' }} />
            <input value={plForm.order_num} onChange={e => setPlForm(p => ({...p, order_num:Number(e.target.value)}))} type="number" placeholder="0" style={{ ...INP_S, textAlign:'center' }} />
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            <button onClick={savePl} disabled={saving} style={{ padding:'7px 18px', background:'#5a67d8', color:'#fff', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'bold', fontFamily:'Cairo, sans-serif' }}>
              {saving ? '...' : '💾 حفظ'}
            </button>
            <button onClick={() => setShowPlForm(false)} style={{ padding:'7px 14px', background:'#f3f4f6', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontFamily:'Cairo, sans-serif' }}>إلغاء</button>
          </div>
        </div>
      )}

      {/* القوائم */}
      {playlists.length === 0 ? (
        <p style={{ color:'#9ca3af', fontSize:'13px' }}>لا توجد قوائم — اضغط "+ إضافة قائمة"</p>
      ) : playlists.map(pl => (
        <div key={pl.id} style={{ border:'1px solid #e5e7eb', borderRadius:'10px', marginBottom:'10px', overflow:'hidden', background:'#fff' }}>
          {/* هيدر القائمة */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', background:'#f9fafb', borderBottom:'1px solid #f3f4f6' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
              <span style={{ fontSize:'18px' }}>{pl.icon}</span>
              <span style={{ fontSize:'13px', fontWeight:'bold', color:'#1f2937' }}>{pl.name_ar}</span>
              {pl.name_en && <span style={{ fontSize:'11px', color:'#6b7280' }}>({pl.name_en})</span>}
            </div>
            <div style={{ display:'flex', gap:'6px', alignItems:'center' }}>
              <button onClick={() => togglePl(pl)}
                style={{ padding:'3px 10px', background:pl.is_active?'#dcfce7':'#fee2e2', color:pl.is_active?'#16a34a':'#dc2626', border:'none', borderRadius:'5px', fontSize:'11px', fontWeight:'bold', cursor:'pointer' }}>
                {pl.is_active ? '✓ مفعّل' : '⏸ موقوف'}
              </button>
              <button onClick={() => { loadItems(pl.id); openAddItem(pl.id); }}
                style={{ padding:'4px 10px', background:'#eef2ff', color:'#5a67d8', border:'none', borderRadius:'5px', fontSize:'11px', fontWeight:'bold', cursor:'pointer' }}>
                + عنصر
              </button>
              <button onClick={() => { setEditPl(pl); setPlForm({ name_ar:pl.name_ar, name_en:pl.name_en, icon:pl.icon, order_num:pl.order_num }); setShowPlForm(true); }}
                style={{ padding:'4px 8px', background:'#fef3c7', color:'#d97706', border:'none', borderRadius:'5px', cursor:'pointer' }}>✏️</button>
              <button onClick={() => deletePl(pl.id)}
                style={{ padding:'4px 8px', background:'#fee2e2', color:'#dc2626', border:'none', borderRadius:'5px', cursor:'pointer' }}>🗑</button>
            </div>
          </div>

          {/* فورم إضافة عنصر */}
          {showItemForm === pl.id && (
            <div style={{ padding:'12px', background:'#fafafa', borderBottom:'1px solid #f3f4f6' }}>
              <div style={{ display:'grid', gridTemplateColumns:'120px 1fr', gap:'8px', marginBottom:'8px' }}>
                <select value={itemForm.type} onChange={e => setItemForm(p => ({...p, type:e.target.value}))} style={INP_S}>
                  <option value="video">🎬 فيديو</option>
                  <option value="image">🖼️ صورة</option>
                  <option value="text">📝 نص</option>
                </select>
                <input value={itemForm.title_ar} onChange={e => setItemForm(p => ({...p, title_ar:e.target.value}))} placeholder="العنوان" style={INP_S} />
              </div>
              <input value={itemForm.content} onChange={e => setItemForm(p => ({...p, content:e.target.value}))}
                placeholder={itemForm.type==='video' ? 'رابط الفيديو: Vimeo / YouTube / ملف Google Drive (مش فولدر)' : itemForm.type==='image' ? 'رابط الصورة' : 'النص'}
                dir="ltr" style={{ ...INP_S, marginBottom:'8px' }} />
              {itemForm.type!=='text' && (
                <textarea value={itemForm.description_ar} onChange={e => setItemForm(p => ({...p, description_ar:e.target.value}))}
                  placeholder="📝 الشرح اللي هيظهر تحت الفيديو في المودال (اختياري)" rows="3"
                  style={{ ...INP_S, marginBottom:'8px', resize:'vertical', lineHeight:'1.7' }} />
              )}
              {/* رابط واتساب + زرار الستاندر */}
              <div style={{ display:'flex', gap:'6px', marginBottom:'8px' }}>
                <input value={itemForm.link_url} onChange={e => setItemForm(p => ({...p, link_url:e.target.value}))}
                  placeholder="💬 رابط واتساب (اختياري)" dir="ltr" style={{ ...INP_S, flex:1 }} />
                {waStd && (
                  <button type="button" onClick={() => setItemForm(p => ({...p, link_url:waStd}))}
                    title="استخدم رقم الواتساب الستاندر من الإعدادات"
                    style={{ padding:'6px 10px', background:'#dcfce7', color:'#16a34a', border:'none', borderRadius:'6px', fontSize:'11px', fontWeight:'bold', cursor:'pointer', whiteSpace:'nowrap', fontFamily:'Cairo, sans-serif' }}>
                    💬 الستاندر
                  </button>
                )}
              </div>
              {/* رابط الدفع بالفيزا (Paymob Payment Link) */}
              <input value={itemForm.pay_url} onChange={e => setItemForm(p => ({...p, pay_url:e.target.value}))}
                placeholder="💳 رابط الدفع بالفيزا (Payment Link من Paymob) — اختياري" dir="ltr" style={{ ...INP_S, marginBottom:'8px' }} />
              {/* مدة الفيديو */}
              {itemForm.type==='video' && (
                <input value={itemForm.duration} onChange={e => setItemForm(p => ({...p, duration:e.target.value}))}
                  placeholder="⏱ مدة الفيديو (مثلاً 5:30)" style={{ ...INP_S, marginBottom:'8px', textAlign:'center' }} />
              )}
              <div style={{ display:'flex', gap:'8px' }}>
                <button onClick={() => saveItem(pl.id)} disabled={saving}
                  style={{ padding:'6px 16px', background:'#5a67d8', color:'#fff', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontWeight:'bold', fontFamily:'Cairo, sans-serif' }}>
                  {saving ? '...' : (editingItem ? '✏️ تعديل' : '💾 حفظ')}
                </button>
                <button onClick={() => { setShowItemForm(null); setEditingItem(null); }}
                  style={{ padding:'6px 12px', background:'#f3f4f6', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px', fontFamily:'Cairo, sans-serif' }}>إلغاء</button>
              </div>
            </div>
          )}

          {/* العناصر */}
          {items[pl.id]?.length > 0 && (
            <div style={{ padding:'8px 14px' }}>
              {items[pl.id].map(item => (
                <div key={item.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 0', borderBottom:'1px solid #f9fafb', fontSize:'12px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                    <span>{item.type==='video'?'🎬':item.type==='image'?'🖼️':'📝'}</span>
                    <span style={{ fontWeight:'600', color:'#1f2937' }}>{item.title_ar||'—'}</span>
                    <span style={{ color:'#9ca3af', fontSize:'11px', direction:'ltr' }}>{item.content?.slice(0,40)}...</span>
                  </div>
                  <div style={{ display:'flex', gap:'6px' }}>
                    <button onClick={() => openEditItem(item, pl.id)}
                      style={{ padding:'3px 10px', background:'#fef3c7', color:'#d97706', border:'none', borderRadius:'4px', cursor:'pointer', fontSize:'11px', fontWeight:'bold' }}>✏️ تعديل</button>
                    <button onClick={() => deleteItem(item.id, pl.id)}
                      style={{ padding:'3px 8px', background:'#fee2e2', color:'#dc2626', border:'none', borderRadius:'4px', cursor:'pointer', fontSize:'11px' }}>حذف</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Dashboard ──────────────────────────────────
// IntroVideoManager — مدير الفيديو الافتتاحي
function IntroVideoManager() {
  const [form,   setForm]   = React.useState({ intro_enabled:false, intro_video_url:'', intro_title:'' });
  const [saving, setSaving] = React.useState(false);
  const [saved,  setSaved]  = React.useState(false);

  React.useEffect(() => {
    (async () => {
      const { supabase } = await import('../../config/supabase');
      const { data } = await supabase.from('settings').select('intro_enabled, intro_video_url, intro_title').eq('id', 1);
      const st = data && data[0];
      if (st) setForm({ intro_enabled: !!st.intro_enabled, intro_video_url: st.intro_video_url || '', intro_title: st.intro_title || '' });
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    const { apiUrl } = await import('../../config/supabase');
    let token = ''; try { token = localStorage.getItem('emp_token') || ''; } catch (e) {}
    try {
      const res = await fetch(apiUrl('/api/admin/settings-save'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, settings: {
          intro_enabled: form.intro_enabled,
          intro_video_url: form.intro_video_url,
          intro_title: form.intro_title,
        } }),
      });
      const r = await res.json().catch(() => ({}));
      setSaving(false);
      if (res.ok && r.ok) {
        setSaved(true); setTimeout(() => setSaved(false), 2500);
        try { sessionStorage.removeItem('gw_intro_seen_v1'); } catch (e) {}
      } else {
        alert(r.message || 'فشل الحفظ');
      }
    } catch (e) {
      setSaving(false); alert('فشل الاتصال بالسيرفر');
    }
  };

  const INP_S = { padding:'8px 12px', border:'1px solid #e5e7eb', borderRadius:'8px', fontSize:'13px', width:'100%', boxSizing:'border-box', fontFamily:'Cairo, sans-serif', outline:'none' };

  return (
    <div style={{ marginBottom:'28px' }}>
      <h3 style={{ fontSize:'15px', fontWeight:'bold', color:'#1F2937', marginBottom:'12px' }}>🎬 الفيديو الافتتاحي</h3>
      <div style={{ background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', padding:'16px' }}>
        <label style={{ display:'flex', alignItems:'center', gap:'10px', cursor:'pointer', marginBottom:'14px' }}>
          <input type="checkbox" checked={form.intro_enabled}
            onChange={e => setForm(f => ({ ...f, intro_enabled: e.target.checked }))}
            style={{ width:'18px', height:'18px', cursor:'pointer' }} />
          <span style={{ fontSize:'13px', fontWeight:'bold', color:'#374151' }}>تفعيل الفيديو الافتتاحي (يظهر أول ما العميل يفتح الموقع)</span>
        </label>
        <div style={{ marginBottom:'12px' }}>
          <label style={{ display:'block', fontSize:'12px', color:'#6b7280', marginBottom:'5px', fontWeight:'bold' }}>عنوان فوق الفيديو (اختياري)</label>
          <input value={form.intro_title} onChange={e => setForm(f => ({ ...f, intro_title: e.target.value }))}
            placeholder="مثلاً: أهلاً بيك في الجوهرة" style={INP_S} />
        </div>
        <div style={{ marginBottom:'14px' }}>
          <label style={{ display:'block', fontSize:'12px', color:'#6b7280', marginBottom:'5px', fontWeight:'bold' }}>رابط الفيديو</label>
          <input value={form.intro_video_url} onChange={e => setForm(f => ({ ...f, intro_video_url: e.target.value }))}
            placeholder="Vimeo / YouTube / ملف Google Drive (مش فولدر)" dir="ltr" style={INP_S} />
          <p style={{ fontSize:'11px', color:'#9ca3af', margin:'6px 0 0' }}>بيشتغل تلقائي وبيظهر مرة واحدة في الجلسة.</p>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
          <button onClick={save} disabled={saving}
            style={{ padding:'9px 22px', background:'linear-gradient(135deg,#5a67d8,#764ba2)', color:'#fff', border:'none', borderRadius:'8px', fontSize:'13px', fontWeight:'bold', cursor:'pointer', fontFamily:'Cairo, sans-serif' }}>
            {saving ? '...' : '💾 حفظ'}
          </button>
          {saved && <span style={{ color:'#16a34a', fontSize:'13px', fontWeight:'bold' }}>✓ اتحفظ — جرّب افتح الصفحة الرئيسية</span>}
        </div>
      </div>
    </div>
  );
}

// ── شريط تابات لوحة التحكم (سلايدات) ─────────────
function TabBar({ view, setView, perms }) {
  const tabs = [
    { id: 'overview',  label: 'نظرة عامة',           icon: '📊',  show: true },
    { id: 'academy',   label: 'الأكاديمية',          icon: '🎓',  show: perms.can_manage_courses },
    { id: 'agency',    label: 'الأجنسي',             icon: '💼',  show: perms.can_manage_services },
    { id: 'customers', label: 'العملاء',             icon: '👥',  show: perms.can_view_customers || perms.can_manage_settings },
    { id: 'staff',     label: 'الموظفين',            icon: '👨‍💼', show: perms.can_manage_employees },
    { id: 'market',    label: 'التسويق',             icon: '🎟️',  show: perms.can_manage_courses },
    { id: 'settings',  label: 'المحتوى والإعدادات',  icon: '⚙️',  show: perms.can_manage_settings || perms.can_manage_courses },
  ].filter(tb => tb.show);
  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', borderBottom: '2px solid #eef0f6', marginBottom: '24px' }}>
      {tabs.map(tb => {
        const on = view === tb.id;
        return (
          <button key={tb.id} onClick={() => setView(tb.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '10px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold',
              fontFamily: 'Tajawal, sans-serif',
              border: 'none', borderRadius: '10px 10px 0 0', marginBottom: '-2px',
              color: on ? '#fff' : '#6b7280',
              background: on ? G1 : 'transparent',
              borderBottom: on ? '3px solid #764ba2' : '3px solid transparent',
              transition: 'all .2s',
            }}>
            <span style={{ fontSize: '16px' }}>{tb.icon}</span> {tb.label}
          </button>
        );
      })}
    </div>
  );
}

export function Dashboard({
  courses, services, customers, enrollments, payments,
  employees, roles, contactMessages,
  categories = [], instructors = [],
  reviews = [], onApproveReview, onDeleteReview,
  lang, isRTL, t, perms, onSetPage,
  onOpenSettings, onOpenReports, onOpenLabels, onOpenKnowledge, onAddCourse, onEditCourse, onDeleteCourse,
  onManageLessons, onAddService, onEditService, onDeleteService,
  serviceReviews = [], agencyWorks = [],
  onSaveServiceReview, onDeleteServiceReview, onApproveServiceReview,
  onSaveAgencyWork, onDeleteAgencyWork, onUploadImage,
  onApprove, onReject,
  onAddEmp, onEditEmp, onDeleteEmp, onToggleEmp,
  onAddRole, onEditRole, onDeleteRole,
  onAddCoupon, onEditCoupon, onDeleteCoupon, onToggleCoupon,
  onToggleCustomer, onDeleteCustomer,
  onCreateCustomer, onUpdateCustomer, onSetCustomerPassword, showToast,
  onAddCategory, onEditCategory, onDeleteCategory,
  onAddInstructor, onEditInstructor, onDeleteInstructor,
}) {
  const [coupons, setCoupons] = React.useState([]);
  const [reviewsFor, setReviewsFor] = React.useState(null);
  const [view, setView] = React.useState('overview');
  // ── مستخدمي الكوبونات: الصف المفتوح + كاش النتائج ──
  const [openCouponCode, setOpenCouponCode] = React.useState(null);
  const [couponUsage, setCouponUsage] = React.useState({});
  const toggleCouponUsage = async (code) => {
    if (openCouponCode === code) { setOpenCouponCode(null); return; }
    setOpenCouponCode(code);
    if (couponUsage[code]) return; // متجاب قبل كده
    try {
      const { apiUrl } = await import('../../config/supabase');
      const token = localStorage.getItem('emp_token') || '';
      const res = await fetch(apiUrl('/api/admin/coupon-usage'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, code }),
      });
      const r = await res.json().catch(() => ({}));
      if (res.status === 401) {
        try { window.dispatchEvent(new CustomEvent('emp-session-expired')); } catch (e) {}
        return;
      }
      setCouponUsage(prev => ({ ...prev, [code]: (res.ok && r.ok) ? (r.usage || []) : [] }));
    } catch (e) {
      setCouponUsage(prev => ({ ...prev, [code]: [] }));
    }
  };
  React.useEffect(() => {
    import('../../config/supabase').then(({ supabase }) => {
      supabase.from('coupons').select('*, courses(name_ar,name_en)').order('created_at', { ascending: false })
        .then(({ data }) => { if (data) setCoupons(data); });
    });
  }, [onAddCoupon, onEditCoupon, onDeleteCoupon, onToggleCoupon]);
  const pending = payments.filter(p => p.status === 'pending');

  // scroll لقسم جوا لوحة التحكم مع تمييزه لحظيًا
  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    el.style.transition = 'box-shadow .3s';
    el.style.boxShadow = '0 0 0 3px #667eea';
    setTimeout(() => { el.style.boxShadow = ''; }, 1400);
  };
  const cLessons = (cid) => courses.find(c => c.id === cid) ? [] : [];
  const lessonCount = (cid) => { return 0; }; // calculated via prop

  return (
    <div style={{ background: 'white', borderRadius: '20px', padding: '28px', boxShadow: '0 10px 32px rgba(0,0,0,0.12)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h2 style={{ fontSize: '22px', fontWeight: 'bold', background: G1, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', margin: 0 }}>📊 {t.dashboard}</h2>
          <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '2px' }}>
            {new Date().toLocaleDateString(lang === 'ar' ? 'ar-EG' : 'en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>
        {perms.can_manage_settings && (
          <button onClick={onOpenSettings} style={{ padding: '8px 14px', background: GG, color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', boxShadow: '0 4px 12px rgba(16,185,129,0.3)' }}>
            <Settings size={13} /> {t.generalSettings}
          </button>
        )}
        {perms.can_manage_settings && onOpenLabels && (
          <button onClick={onOpenLabels} style={{ padding: '8px 14px', background: 'linear-gradient(135deg,#f59e0b,#d97706)', color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', boxShadow: '0 4px 12px rgba(245,158,11,0.3)' }}>
            🏷️ تغيير النصوص
          </button>
        )}
        {perms.can_manage_settings && onOpenKnowledge && (
          <button onClick={onOpenKnowledge} style={{ padding: '8px 14px', background: 'linear-gradient(135deg,#8b5cf6,#6d28d9)', color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', boxShadow: '0 4px 12px rgba(139,92,246,0.3)' }}>
            🤖 المساعد الذكي
          </button>
        )}
        {perms.can_view_dashboard && onOpenReports && (
          <button onClick={onOpenReports} style={{ padding: '8px 14px', background: 'linear-gradient(135deg,#5a67d8,#764ba2)', color: 'white', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', boxShadow: '0 4px 12px rgba(90,103,216,0.3)' }}>
            📊 التقارير
          </button>
        )}
      </div>

      <TabBar view={view} setView={setView} perms={perms} />

      {/* Stats — دبل كلك يفتح صفحة/قسم كل بطاقة */}
      {view === 'overview' && (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: '12px', marginBottom: '28px' }}>
        {[
          { label: t.coursesLabel,     value: courses.length,    gradient: 'linear-gradient(135deg,#667eea,#764ba2)', icon: '🎓', sub: lang === 'ar' ? 'كورس نشط' : 'active',     go: () => setView('academy') },
          { label: t.servicesLabel,    value: services.length,   gradient: 'linear-gradient(135deg,#764ba2,#f093fb)', icon: '💼', sub: lang === 'ar' ? 'خدمة' : 'services',       go: () => setView('agency') },
          { label: t.customersLabel,   value: customers.length,  gradient: 'linear-gradient(135deg,#10b981,#059669)', icon: '👥', sub: lang === 'ar' ? 'عميل مسجل' : 'registered',  go: () => setView('customers') },
          { label: t.enrollmentsLabel, value: enrollments.filter(e => e.status === 'active').length, gradient: 'linear-gradient(135deg,#f59e0b,#d97706)', icon: '✅', sub: lang === 'ar' ? 'اشتراك فعال' : 'active', go: () => setView('customers') },
          { label: t.pendingPayments,  value: pending.length,    gradient: 'linear-gradient(135deg,#ef4444,#dc2626)', icon: '⏳', sub: lang === 'ar' ? 'في الانتظار' : 'pending',   go: () => setView('overview') },
          { label: t.manageEmployees,  value: employees.length,  gradient: GE, icon: '👨‍💼', sub: lang === 'ar' ? 'موظف' : 'staff',                                            go: () => setView('staff') },
        ].map((s, i) => (
          <div key={i} onClick={s.go} title={lang === 'ar' ? 'اضغط للفتح' : 'Click to open'}
            style={{ background: s.gradient, borderRadius: '14px', padding: '16px', textAlign: 'center', boxShadow: '0 6px 20px rgba(0,0,0,0.12)', position: 'relative', overflow: 'hidden', cursor: 'pointer', transition: 'transform .15s, box-shadow .15s' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(0,0,0,0.22)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.12)'; }}>
            <div style={{ position: 'absolute', bottom: '-8px', [isRTL ? 'left' : 'right']: '-8px', fontSize: '40px', opacity: 0.2 }}>{s.icon}</div>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.8)', marginBottom: '4px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
            <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'white', lineHeight: '1' }}>{s.value}</div>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)', marginTop: '3px' }}>{s.sub}</div>
          </div>
        ))}
      </div>
      )}

      {/* Pending Payments */}
      {view === 'overview' && pending.length > 0 && (
        <div id="sec-pending" style={{ marginBottom: '28px', background: '#FFF7ED', borderRadius: '14px', padding: '18px', border: '1px solid #FED7AA' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 'bold', color: '#92400E', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            ⏳ {t.pendingPayments}
            <span style={{ background: '#EF4444', color: 'white', borderRadius: '20px', padding: '1px 8px', fontSize: '11px', fontWeight: 'bold' }}>{pending.length}</span>
          </h3>
          <div style={{ display: 'grid', gap: '8px' }}>
            {pending.map(pay => {
              const cust   = customers.find(c => c.id === pay.customer_id);
              const course  = courses.find(c => c.id === pay.course_id);
              return (
                <div key={pay.id} style={{ background: 'white', border: `1px solid ${pay.method === 'card' ? '#BFDBFE' : '#FED7AA'}`, padding: '12px 14px', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#1F2937' }}>{cust?.name} — {lang === 'ar' ? course?.name_ar : course?.name_en}</div>
                    <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span>{pay.amount} جنيه</span>
                      <span style={{ background: pay.method === 'card' ? '#DBEAFE' : '#FEF3C7', color: pay.method === 'card' ? '#1D4ED8' : '#92400E', padding: '1px 6px', borderRadius: '4px', fontWeight: 'bold', fontSize: '10px' }}>
                        {pay.method === 'card' ? '💳 فيزا' : pay.method === 'vodafone' ? '📱 فودافون' : '🏦 تحويل'}
                      </span>
                      {pay.receipt_note && <span>· {pay.receipt_note}</span>}
                    </div>
                  </div>
                  {pay.method === 'card' ? (
                    <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '6px', padding: '6px 12px', fontSize: '11px', color: '#1D4ED8', fontWeight: 'bold' }}>
                      ⏳ {lang === 'ar' ? 'تأكيد أوتوماتيك عبر Paymob' : 'Auto-confirm via Paymob'}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={() => onApprove(pay)} style={{ padding: '6px 12px', background: GG, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <CheckSquare size={12} /> {t.approve}
                      </button>
                      <button onClick={() => onReject(pay.id)} style={{ padding: '6px 12px', background: '#EF4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>{t.reject}</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Courses */}
      {view === 'academy' && perms.can_manage_courses && (
        <Section title={t.manageCourses} onAdd={onAddCourse} addLabel={t.addCourse} addBg={G1}>
          {courses.map(c => (
            <Row key={c.id} color="#667eea" icon={c.icon}
              title={lang === 'ar' ? c.name_ar : c.name_en}
              sub={`${c.price} جنيه`}>
              <button onClick={() => onManageLessons(c)} style={btnStyle('#10b981')}><List size={12} /> {t.lessons}</button>
              <button onClick={() => onEditCourse(c)} style={btnStyle('#3B82F6')}><Edit2 size={12} /></button>
              <button onClick={() => onDeleteCourse(c.id)} style={btnStyle('#EF4444')}><Trash2 size={12} /></button>
            </Row>
          ))}
        </Section>
      )}

      {/* 🗂️ Categories */}
      {view === 'academy' && perms.can_manage_courses && (
        <Section title="🗂️ التصنيفات" onAdd={onAddCategory} addLabel="إضافة تصنيف" addBg={G1}>
          {categories.map(c => (
            <Row key={c.id} color="#667eea" icon={c.icon}
              title={lang === 'ar' ? c.name_ar : (c.name_en || c.name_ar)}
              sub={`${courses.filter(co => co.category_id === c.id).length} كورس`}>
              <button onClick={() => onEditCategory(c)} style={btnStyle('#3B82F6')}><Edit2 size={12} /></button>
              <button onClick={() => onDeleteCategory(c.id)} style={btnStyle('#EF4444')}><Trash2 size={12} /></button>
            </Row>
          ))}
          {!categories.length && <div style={{ padding: '14px', textAlign: 'center', color: '#9CA3AF', fontSize: '13px' }}>لا يوجد تصنيفات — اضغط "إضافة تصنيف"</div>}
        </Section>
      )}

      {/* 👨‍🏫 Instructors */}
      {view === 'academy' && perms.can_manage_courses && (
        <Section title="👨‍🏫 المدربين" onAdd={onAddInstructor} addLabel="إضافة مدرب" addBg={G1}>
          {instructors.map(i => (
            <Row key={i.id} color="#764ba2"
              icon={i.avatar_url ? <img src={i.avatar_url} alt={i.name_ar} style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }} /> : '👨‍🏫'}
              title={i.name_ar}
              sub={`${i.title_ar || ''}${i.title_ar ? ' • ' : ''}${courses.filter(co => co.instructor_id === i.id).length} كورس`}>
              <button onClick={() => onEditInstructor(i)} style={btnStyle('#3B82F6')}><Edit2 size={12} /></button>
              <button onClick={() => onDeleteInstructor(i.id)} style={btnStyle('#EF4444')}><Trash2 size={12} /></button>
            </Row>
          ))}
          {!instructors.length && <div style={{ padding: '14px', textAlign: 'center', color: '#9CA3AF', fontSize: '13px' }}>لا يوجد مدربين — اضغط "إضافة مدرب"</div>}
        </Section>
      )}

      {/* Services */}
      {view === 'agency' && perms.can_manage_services && (
        <Section title={t.manageServices} onAdd={onAddService} addLabel={t.addService} addBg={G2}>
          {services.map(s => (
            <div key={s.id}>
              <Row color="#764ba2" icon={s.icon}
                title={lang === 'ar' ? s.name_ar : s.name_en}
                sub={`${t.from} $${s.price}`}>
                <button onClick={() => setReviewsFor(reviewsFor === s.id ? null : s.id)} style={btnStyle(reviewsFor === s.id ? '#7c3aed' : '#A78BFA')} title="آراء الباقة">
                  💬{serviceReviews.filter(r => r.service_id === s.id && r.is_approved === false).length > 0 ? ` (${serviceReviews.filter(r => r.service_id === s.id && r.is_approved === false).length})` : ''}
                </button>
                <button onClick={() => onEditService(s)} style={btnStyle('#3B82F6')}><Edit2 size={12} /></button>
                <button onClick={() => onDeleteService(s.id)} style={btnStyle('#EF4444')}><Trash2 size={12} /></button>
              </Row>
              {reviewsFor === s.id && (
                <ServiceReviewsManager
                  service={s}
                  reviews={serviceReviews.filter(r => r.service_id === s.id)}
                  onSave={onSaveServiceReview}
                  onDelete={onDeleteServiceReview}
                  onApprove={onApproveServiceReview}
                  onUploadImage={onUploadImage}
                />
              )}
            </div>
          ))}
        </Section>
      )}

      {/* 🎬 أعمالنا */}
      {view === 'agency' && perms.can_manage_services && (
        <AgencyWorksManager
          works={agencyWorks}
          services={services}
          onSave={onSaveAgencyWork}
          onDelete={onDeleteAgencyWork}
          onUploadImage={onUploadImage}
        />
      )}

      {/* 🎟️ Coupons */}
      {view === 'market' && perms.can_manage_courses && (
        <div style={{ marginBottom: '28px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '15px', fontWeight: 'bold', color: '#1F2937', margin: 0 }}>🎟️ كوبونات الخصم</h3>
            <button onClick={onAddCoupon}
              style={{ padding: '7px 14px', background: 'linear-gradient(135deg,#5a67d8,#764ba2)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Plus size={13} /> إضافة كوبون
            </button>
          </div>
          <div style={{ fontSize: '11.5px', color: '#6B7280', marginBottom: '8px' }}>💡 اضغط على أي كوبون لعرض مين استخدمه</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: '#F9FAFB' }}>
                  {['الكود', 'النوع', 'القيمة', 'ينطبق على', 'الاستخدام', 'الانتهاء', 'الحالة', ''].map((h, i) => (
                    <th key={i} style={{ padding: '9px 12px', textAlign: 'right', fontWeight: 'bold', color: '#374151', borderBottom: '2px solid #E5E7EB', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {coupons.map(c => (
                  <React.Fragment key={c.id}>
                  <tr style={{ borderBottom: '1px solid #F3F4F6', cursor: 'pointer', background: openCouponCode === c.code ? '#F5F3FF' : 'white' }}
                    onMouseEnter={e => { if (openCouponCode !== c.code) e.currentTarget.style.background = '#F9FAFB'; }}
                    onMouseLeave={e => { if (openCouponCode !== c.code) e.currentTarget.style.background = 'white'; }}
                    onClick={(e) => { if (!e.target.closest('button')) toggleCouponUsage(c.code); }}
                    onDoubleClick={(e) => { if (!e.target.closest('button')) toggleCouponUsage(c.code); }}>
                    <td style={{ padding: '9px 12px' }}>
                      <span style={{ marginInlineEnd: '6px', display: 'inline-block', transition: 'transform .15s', transform: openCouponCode === c.code ? 'rotate(90deg)' : 'none', color: '#5a67d8', fontWeight: 'bold' }}>◂</span>
                      <span style={{ background: '#EEF2FF', color: '#5a67d8', padding: '3px 10px', borderRadius: '6px', fontWeight: 'bold', letterSpacing: '1px', fontFamily: 'monospace', fontSize: '13px' }}>{c.code}</span>
                    </td>
                    <td style={{ padding: '9px 12px', color: '#374151' }}>{c.type === 'percent' ? '📊 نسبة %' : '💰 مبلغ ثابت'}</td>
                    <td style={{ padding: '9px 12px', fontWeight: 'bold', color: '#16a34a' }}>{c.type === 'percent' ? `${c.value}%` : `${c.value} جنيه`}</td>
                    <td style={{ padding: '9px 12px', color: '#6B7280' }}>{c.course_id ? (lang === 'ar' ? c.courses?.name_ar : c.courses?.name_en) || '—' : '🌍 كل الكورسات'}</td>
                    <td style={{ padding: '9px 12px', color: '#374151' }}>{c.used_count || 0}{c.max_uses ? ` / ${c.max_uses}` : ' / ∞'}</td>
                    <td style={{ padding: '9px 12px', color: '#6B7280' }}>{c.expires_at ? new Date(c.expires_at).toLocaleDateString('ar-EG') : '—'}</td>
                    <td style={{ padding: '9px 12px' }}>
                      <button onClick={() => onToggleCoupon(c)}
                        style={{ padding: '3px 10px', background: c.is_active ? '#dcfce7' : '#fee2e2', color: c.is_active ? '#16a34a' : '#dc2626', border: 'none', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer' }}>
                        {c.is_active ? '✅ مفعّل' : '⏸️ موقوف'}
                      </button>
                    </td>
                    <td style={{ padding: '9px 12px' }}>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        <button onClick={() => onEditCoupon(c)} style={btnStyle('#3B82F6')}><Edit2 size={12} /></button>
                        <button onClick={() => onDeleteCoupon(c.id)} style={btnStyle('#EF4444')}><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                  {openCouponCode === c.code && (
                    <tr>
                      <td colSpan="8" style={{ padding: '0 12px 14px', background: '#FAFAFF' }}>
                        <div style={{ background: 'white', border: '1px solid #E9E5FF', borderRadius: '12px', overflow: 'hidden', marginTop: '6px', boxShadow: '0 2px 10px rgba(102,126,234,.07)' }}>
                          <div style={{ padding: '10px 14px', background: 'linear-gradient(135deg,#667eea12,#764ba212)', fontWeight: 'bold', fontSize: '12.5px', color: '#4c1d95', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>👥 مستخدمي الكوبون {c.code}</span>
                            {couponUsage[c.code] && <span style={{ color: '#6B7280', fontWeight: 'normal' }}>{couponUsage[c.code].length} استخدام</span>}
                          </div>
                          {!couponUsage[c.code] ? (
                            <div style={{ padding: '18px', textAlign: 'center', color: '#9CA3AF', fontSize: '12.5px' }}>
                              <Loader size={16} style={{ animation: 'gwspin 1s linear infinite', verticalAlign: 'middle', marginInlineEnd: '6px' }} />
                              جاري التحميل…
                            </div>
                          ) : couponUsage[c.code].length === 0 ? (
                            <div style={{ padding: '18px', textAlign: 'center', color: '#9CA3AF', fontSize: '12.5px' }}>محدش استخدم الكوبون ده لسه</div>
                          ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                              <thead>
                                <tr style={{ background: '#F8FAFC' }}>
                                  {['#', 'الاسم', 'اليوزرنيم', 'رقم التليفون', 'تاريخ الاستخدام', 'المبلغ', 'الحالة'].map((h, i) => (
                                    <th key={i} style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 'bold', color: '#64748b', borderBottom: '1.5px solid #E2E8F0', whiteSpace: 'nowrap', fontSize: '11.5px' }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {couponUsage[c.code].map((u, i) => (
                                  <tr key={u.payment_id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                                    <td style={{ padding: '8px 12px', color: '#94a3b8', fontWeight: 'bold' }}>{i + 1}</td>
                                    <td style={{ padding: '8px 12px', fontWeight: 'bold', color: '#1e293b' }}>{u.name}</td>
                                    <td style={{ padding: '8px 12px' }}><span style={{ fontFamily: 'monospace', background: '#F1F5F9', padding: '2px 8px', borderRadius: '5px', color: '#764ba2', fontWeight: 'bold', fontSize: '11.5px' }}>{u.username}</span></td>
                                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: '#374151' }}>{u.phone}</td>
                                    <td style={{ padding: '8px 12px', color: '#6B7280' }}>{u.used_at ? new Date(u.used_at).toLocaleString('ar-EG', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                                    <td style={{ padding: '8px 12px', fontWeight: 'bold', color: u.amount > 0 ? '#16a34a' : '#6B7280' }}>{u.amount > 0 ? `${u.amount} ج` : 'مجاني'}</td>
                                    <td style={{ padding: '8px 12px' }}>
                                      <span style={{ background: u.status === 'paid' ? '#dcfce7' : '#fef3c7', color: u.status === 'paid' ? '#16a34a' : '#b45309', padding: '2px 8px', borderRadius: '10px', fontSize: '10.5px', fontWeight: 'bold' }}>
                                        {u.status === 'paid' ? '✅ مدفوع' : '⏳ معلّق'}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
                {!coupons.length && (
                  <tr><td colSpan="8" style={{ padding: '20px', textAlign: 'center', color: '#9CA3AF' }}>لا يوجد كوبونات بعد — اضغط "إضافة كوبون" لإنشاء أول كوبون</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ⭐ Reviews moderation */}
      {view === 'academy' && perms.can_manage_courses && (
        <div style={{ marginBottom: '28px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 'bold', color: '#1F2937', marginBottom: '12px' }}>
            ⭐ آراء الطلاب
            {reviews.filter(r => r.is_approved !== true).length > 0 && (
              <span style={{ background: '#FEF3C7', color: '#D97706', fontSize: '11px', padding: '2px 8px', borderRadius: '12px', marginInlineStart: '8px' }}>
                {reviews.filter(r => r.is_approved !== true).length} في انتظار الموافقة
              </span>
            )}
          </h3>
          <div style={{ display: 'grid', gap: '8px' }}>
            {[...reviews].sort((a, b) => (a.is_approved === b.is_approved ? 0 : a.is_approved ? 1 : -1)).map(r => {
              const course = courses.find(c => c.id === r.course_id);
              return (
                <div key={r.id} style={{ background: r.is_approved ? '#F9FAFB' : '#FFFBEB', padding: '12px 14px', borderRadius: '10px', borderInlineStart: `3px solid ${r.is_approved ? '#10b981' : '#F59E0B'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ fontWeight: 'bold', fontSize: '13px', color: '#1F2937' }}>{r.student_name || 'طالب'}</span>
                        <span style={{ display: 'flex', gap: '1px' }}>
                          {[1, 2, 3, 4, 5].map(n => <span key={n} style={{ color: n <= r.rating ? '#F59E0B' : '#D1D5DB', fontSize: '12px' }}>★</span>)}
                        </span>
                        <span style={{ fontSize: '11px', color: '#6B7280' }}>• {course ? (lang === 'ar' ? course.name_ar : course.name_en) : '—'}</span>
                        {!r.is_approved && <span style={{ fontSize: '10px', background: '#FEF3C7', color: '#D97706', padding: '1px 7px', borderRadius: '10px', fontWeight: 'bold' }}>منتظر</span>}
                      </div>
                      {r.comment_ar && <div style={{ fontSize: '12px', color: '#4B5563', lineHeight: 1.6 }}>{r.comment_ar}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      {!r.is_approved && (
                        <button onClick={() => onApproveReview(r.id)} style={btnStyle('#10b981')}><CheckSquare size={12} /> موافقة</button>
                      )}
                      <button onClick={() => { if (window.confirm('حذف الرأي؟')) onDeleteReview(r.id); }} style={btnStyle('#EF4444')}><Trash2 size={12} /></button>
                    </div>
                  </div>
                </div>
              );
            })}
            {!reviews.length && <div style={{ textAlign: 'center', padding: '20px', color: '#9CA3AF', fontSize: '13px' }}>لا توجد آراء بعد</div>}
          </div>
        </div>
      )}

      {/* 📱 Sidebar Manager */}
      {view === 'settings' && perms.can_manage_settings && <IntroVideoManager />}
      {view === 'settings' && perms.can_manage_courses && <SidebarManager />}

      {/* Customers */}
      {view === 'customers' && perms.can_view_customers && (
        <CustomersManager
          customers={customers}
          enrollments={enrollments}
          courses={courses}
          lang={lang}
          isRTL={isRTL}
          onCreate={onCreateCustomer}
          onUpdate={onUpdateCustomer}
          onSetPassword={onSetCustomerPassword}
          onToggle={onToggleCustomer}
          onDelete={onDeleteCustomer}
          showToast={showToast}
        />
      )}

      {/* Contact Messages */}
      {view === 'customers' && perms.can_manage_settings && contactMessages.length > 0 && (
        <div style={{ marginBottom: '28px' }}>
          <div style={{ height: '1px', background: '#E5E7EB', marginBottom: '20px' }} />
          <h3 style={{ fontSize: '15px', fontWeight: 'bold', color: '#1F2937', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <MessageSquare size={16} style={{ color: '#667eea' }} /> {t.contactMessages}
            <span style={{ background: '#EEF2FF', color: '#667eea', borderRadius: '20px', padding: '1px 8px', fontSize: '11px', fontWeight: 'bold' }}>{contactMessages.length}</span>
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: '10px' }}>
            {contactMessages.slice(0, 9).map(msg => (
              <div key={msg.id} style={{ background: '#F9FAFB', borderRadius: '12px', padding: '14px', border: '1px solid #E5E7EB' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                  <span style={{ fontWeight: 'bold', fontSize: '13px', color: '#1F2937' }}>{msg.name}</span>
                  <span style={{ fontSize: '10px', color: '#9CA3AF' }}>{msg.created_at ? new Date(msg.created_at).toLocaleDateString('ar-EG') : ''}</span>
                </div>
                {msg.service && <div style={{ fontSize: '10px', background: '#EEF2FF', color: '#667eea', display: 'inline-block', padding: '1px 6px', borderRadius: '4px', marginBottom: '5px' }}>{msg.service}</div>}
                <div style={{ fontSize: '12px', color: '#374151', lineHeight: '1.5', marginBottom: '6px' }}>{msg.message}</div>
                <div style={{ fontSize: '11px', color: '#9CA3AF', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {msg.phone && <span>📱 {msg.phone}</span>}
                  {msg.email && <span>✉️ {msg.email}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Roles & Employees */}
      {view === 'staff' && perms.can_manage_employees && (
        <StaffManager
          employees={employees}
          roles={roles}
          lang={lang}
          isRTL={isRTL}
          t={t}
          onAddEmp={onAddEmp}
          onEditEmp={onEditEmp}
          onDeleteEmp={onDeleteEmp}
          onToggleEmp={onToggleEmp}
          onAddRole={onAddRole}
          onEditRole={onEditRole}
          onDeleteRole={onDeleteRole}
        />
      )}
    </div>
  );
}

// ── LessonsManager ──────────────────────────────
export function LessonsManager({ managingCourse, lessons, lang, isRTL, t, onBack, onAddLesson, onEditLesson, onDeleteLesson, studentWorks = [], onSaveWork, onDeleteWork, onUploadImage }) {
  const cLessons = lessons.filter(l => l.course_id === managingCourse.id).sort((a, b) => a.order_num - b.order_num);

  return (
    <div style={{ background: 'white', borderRadius: '20px', padding: '28px', boxShadow: '0 10px 32px rgba(0,0,0,0.12)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button onClick={onBack} style={{ background: G1, border: 'none', borderRadius: '8px', padding: '8px 12px', color: 'white', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <ArrowRight size={13} style={{ transform: isRTL ? 'none' : 'rotate(180deg)' }} /> {t.dashboard}
          </button>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', background: G1, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', margin: 0 }}>
              {lang === 'ar' ? managingCourse.name_ar : managingCourse.name_en}
            </h2>
            <div style={{ fontSize: '12px', color: '#6B7280' }}>{t.manageLessons}</div>
          </div>
        </div>
        <button onClick={onAddLesson} style={{ padding: '8px 14px', background: G1, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold', fontSize: '12px', boxShadow: '0 4px 12px rgba(102,126,234,0.35)' }}>
          <Plus size={13} /> {t.addLesson}
        </button>
      </div>

      <div style={{ display: 'grid', gap: '8px' }}>
        {cLessons.map((lesson, idx) => (
          <div key={lesson.id} style={{ background: '#F9FAFB', padding: '12px 14px', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', borderInlineStart: '3px solid #667eea' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '28px', height: '28px', background: G1, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '12px', fontWeight: 'bold', flexShrink: 0 }}>{idx + 1}</div>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#1F2937' }}>{lang === 'ar' ? lesson.title_ar : lesson.title_en}</div>
                <div style={{ fontSize: '11px', color: '#6B7280', display: 'flex', gap: '8px', marginTop: '2px' }}>
                  <span>🎬 {lesson.video_source || '—'}</span>
                  {lesson.duration && <span>⏱ {lesson.duration}</span>}
                  {lesson.is_free && <span style={{ color: '#059669', fontWeight: 'bold' }}>✓ {t.free}</span>}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '5px' }}>
              <button onClick={() => onEditLesson(lesson)} style={btnStyle('#3B82F6')}><Edit2 size={12} /></button>
              <button onClick={() => onDeleteLesson(lesson.id)} style={btnStyle('#EF4444')}><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
        {!cLessons.length && <div style={{ textAlign: 'center', padding: '30px', color: '#9CA3AF' }}>لا توجد حلقات — اضغط "إضافة حلقة"</div>}
      </div>

      {/* ═══ أعمال طلاب هذا الكورس ═══ */}
      <StudentWorksManager
        course={managingCourse}
        works={studentWorks.filter(w => w.course_id === managingCourse.id)}
        onSaveWork={onSaveWork}
        onDeleteWork={onDeleteWork}
        onUploadImage={onUploadImage}
      />
    </div>
  );
}

// ── StudentWorksManager (داخل إدارة الكورس) ──
function StudentWorksManager({ course, works, onSaveWork, onDeleteWork, onUploadImage }) {
  const [showForm, setShowForm] = React.useState(false);
  const [editing, setEditing]   = React.useState(null);
  const [form, setForm] = React.useState({ student_name: '', title_ar: '', media_type: 'image', media_url: '' });
  const [uploading, setUploading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const openAdd  = () => { setEditing(null); setForm({ student_name: '', title_ar: '', media_type: 'image', media_url: '' }); setShowForm(true); };
  const openEdit = (w) => { setEditing(w); setForm({ student_name: w.student_name || '', title_ar: w.title_ar || '', media_type: w.media_type || 'image', media_url: w.media_url || '' }); setShowForm(true); };

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('الصورة أكبر من 5MB'); return; }
    setUploading(true);
    const url = await onUploadImage(file, 'work');
    if (url) setForm(f => ({ ...f, media_url: url, media_type: 'image' }));
    else alert('فشل الرفع');
    setUploading(false);
  };

  const submit = async () => {
    if (!form.media_url) { alert('ارفع صورة أو حط رابط الفيديو'); return; }
    setSaving(true);
    await onSaveWork({
      course_id: course.id,
      student_name: form.student_name || null,
      title_ar: form.title_ar || null,
      media_type: form.media_type,
      media_url: form.media_url,
      is_active: true,
    }, editing);
    setSaving(false); setShowForm(false); setEditing(null);
  };

  return (
    <div style={{ marginTop: '28px', borderTop: '2px solid #F3F4F6', paddingTop: '22px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: 'bold', color: '#1F2937', margin: 0 }}>🎬 أعمال طلاب هذا الكورس</h3>
        <button onClick={openAdd} style={{ padding: '8px 14px', background: G1, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold', fontSize: '12px' }}>
          <Plus size={13} /> إضافة عمل
        </button>
      </div>

      {/* الفورم */}
      {showForm && (
        <div style={{ background: '#F9FAFB', borderRadius: '12px', padding: '16px', marginBottom: '14px', border: '1px solid #E5E7EB' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151', display: 'block', marginBottom: '4px' }}>اسم الطالب</label>
              <input value={form.student_name} onChange={e => setForm(f => ({ ...f, student_name: e.target.value }))} placeholder="اسم الطالب" style={INP} />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151', display: 'block', marginBottom: '4px' }}>عنوان العمل</label>
              <input value={form.title_ar} onChange={e => setForm(f => ({ ...f, title_ar: e.target.value }))} placeholder="عنوان العمل" style={INP} />
            </div>
          </div>

          {/* نوع الوسيط */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
            {[{ id: 'image', label: '🖼️ صورة' }, { id: 'video', label: '🎬 فيديو' }].map(o => (
              <button key={o.id} type="button" onClick={() => setForm(f => ({ ...f, media_type: o.id, media_url: '' }))}
                style={{ padding: '7px 16px', borderRadius: '8px', border: `1.5px solid ${form.media_type === o.id ? '#667eea' : '#E5E7EB'}`, background: form.media_type === o.id ? '#EEF2FF' : 'white', color: form.media_type === o.id ? '#667eea' : '#6B7280', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
                {o.label}
              </button>
            ))}
          </div>

          {/* صورة: رفع — فيديو: رابط */}
          {form.media_type === 'image' ? (
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: uploading ? '#9CA3AF' : '#EEF2FF', color: uploading ? 'white' : '#667eea', borderRadius: '8px', padding: '9px 16px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
                {uploading ? <><Loader size={13} style={SS} /> رفع...</> : '📤 ارفع صورة العمل'}
                <input type="file" accept="image/*" onChange={e => handleUpload(e.target.files?.[0])} disabled={uploading} style={{ display: 'none' }} />
              </label>
              {form.media_url && <img src={form.media_url} alt="work" style={{ display: 'block', marginTop: '8px', width: '120px', height: '80px', objectFit: 'cover', borderRadius: '8px' }} />}
            </div>
          ) : (
            <div style={{ marginBottom: '12px' }}>
              <input value={form.media_url} onChange={e => setForm(f => ({ ...f, media_url: e.target.value }))} placeholder="رابط فيديو Vimeo أو YouTube" style={INP} />
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={submit} disabled={saving} style={{ flex: 1, padding: '10px', background: saving ? '#9CA3AF' : G1, color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'جاري الحفظ...' : (editing ? 'حفظ التعديل' : 'إضافة')}
            </button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} style={{ padding: '10px 18px', background: '#E5E7EB', color: '#374151', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>إلغاء</button>
          </div>
        </div>
      )}

      {/* قائمة الأعمال */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: '10px' }}>
        {works.map(w => (
          <div key={w.id} style={{ background: '#F9FAFB', borderRadius: '10px', overflow: 'hidden', border: '1px solid #F3F4F6' }}>
            {w.media_type === 'image'
              ? <img src={w.media_url} alt={w.title_ar} style={{ width: '100%', height: '90px', objectFit: 'cover' }} />
              : <div style={{ height: '90px', background: '#1F2937', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '24px' }}>🎬</div>}
            <div style={{ padding: '8px' }}>
              {w.title_ar && <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#1F2937' }}>{w.title_ar}</div>}
              {w.student_name && <div style={{ fontSize: '10px', color: '#6B7280' }}>👤 {w.student_name}</div>}
              <div style={{ display: 'flex', gap: '4px', marginTop: '6px' }}>
                <button onClick={() => openEdit(w)} style={{ ...btnStyle('#3B82F6'), flex: 1, justifyContent: 'center' }}><Edit2 size={11} /></button>
                <button onClick={() => { if (window.confirm('حذف العمل؟')) onDeleteWork(w.id); }} style={{ ...btnStyle('#EF4444'), flex: 1, justifyContent: 'center' }}><Trash2 size={11} /></button>
              </div>
            </div>
          </div>
        ))}
        {!works.length && <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '20px', color: '#9CA3AF', fontSize: '13px' }}>لا توجد أعمال — اضغط "إضافة عمل"</div>}
      </div>
    </div>
  );
}

// ── Shared helpers ──
const btnStyle = (bg) => ({
  padding: '5px 9px', background: bg, color: 'white',
  border: 'none', borderRadius: '6px', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: '3px',
  fontSize: '11px', fontWeight: 'bold',
});

function Section({ title, onAdd, addLabel, addBg, children }) {
  return (
    <div style={{ marginBottom: '28px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: 'bold', color: '#1F2937', margin: 0 }}>{title}</h3>
        <button onClick={onAdd} style={{ padding: '7px 12px', background: addBg, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 'bold', fontSize: '12px' }}>
          <Plus size={13} /> {addLabel}
        </button>
      </div>
      <div style={{ display: 'grid', gap: '8px' }}>{children}</div>
    </div>
  );
}

function Row({ color, icon, title, sub, children }) {
  return (
    <div style={{ background: '#F9FAFB', padding: '12px 14px', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', borderInlineStart: `3px solid ${color}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontSize: '22px' }}>{icon}</span>
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '13px', color: '#1F2937' }}>{title}</div>
          <div style={{ color, fontSize: '12px' }}>{sub}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '5px' }}>{children}</div>
    </div>
  );
}

// ── KnowledgeManager ─────────────────────────────────────────────────────
// embedded=true → بيترندر جوه BotManager كتاب (من غير overlay ولا زرار إغلاق)
export function KnowledgeManager({ lang, isRTL, onClose, embedded = false }) {
  const [items,    setItems]    = React.useState([]);
  const [loading,  setLoading]  = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [editing,  setEditing]  = React.useState(null);
  const [saving,   setSaving]   = React.useState(false);
  const [form, setForm] = React.useState({ category: 'عام', title: '', content: '', sort_order: 0 });

  const CATS = ['عام', 'كورس', 'خدمة', 'دفع', 'تواصل', 'أسئلة شائعة'];

  React.useEffect(() => {
    import('../../config/supabase').then(({ supabase }) => {
      supabase.from('ai_knowledge').select('*').order('sort_order').then(({ data }) => {
        if (data) setItems(data);
        setLoading(false);
      });
    });
  }, []);

  const save = async () => {
    if (!form.title || !form.content) return;
    setSaving(true);
    const row = editing
      ? { ...form, updated_at: new Date().toISOString(), id: editing.id }
      : form;
    const r = await contentSave('ai_knowledge', row);
    setSaving(false);
    if (r.error) { alert(r.error); return; }
    if (editing) setItems(p => p.map(i => i.id === r.row.id ? r.row : i));
    else setItems(p => [...p, r.row]);
    setShowForm(false); setEditing(null);
    setForm({ category: 'عام', title: '', content: '', sort_order: 0 });
  };

  const del = async (id) => {
    if (!window.confirm('حذف هذا العنصر؟')) return;
    const r = await contentDelete('ai_knowledge', id);
    if (r.error) { alert(r.error); return; }
    setItems(p => p.filter(i => i.id !== id));
  };

  const toggle = async (item) => {
    const r = await contentSave('ai_knowledge', { id: item.id, is_active: !item.is_active });
    if (r.error) { alert(r.error); return; }
    setItems(p => p.map(i => i.id === item.id ? { ...i, is_active: !i.is_active } : i));
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ category: item.category, title: item.title, content: item.content, sort_order: item.sort_order || 0 });
    setShowForm(true);
  };

  const INP = { padding: '8px 10px', border: '1px solid #334155', borderRadius: '8px', fontSize: '13px', width: '100%', boxSizing: 'border-box', outline: 'none', background: '#1e293b', color: 'white' };

  const Wrapper = ({ children }) => embedded
    ? <div style={{ direction: 'rtl' }}>{children}</div>
    : (
      <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '20px', overflowY: 'auto' }}
        onClick={onClose}>
        <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: '750px', background: '#0f172a', borderRadius: '20px', padding: '24px', boxShadow: '0 25px 60px rgba(0,0,0,0.5)', marginTop: '20px', direction: 'rtl' }}>
          {children}
        </div>
      </div>
    );

  return (
    <Wrapper>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h2 style={{ color: 'white', fontWeight: 'bold', fontSize: embedded ? '15px' : '18px', margin: 0 }}>💡 قاعدة معرفة المساعد الذكي</h2>
            <p style={{ color: '#94a3b8', fontSize: '12px', margin: '4px 0 0' }}>أضف معلومات الكورسات والخدمات عشان المساعد يجاوب بدقة — بتظهر في الويب وتليجرام</p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => { setEditing(null); setForm({ category: 'عام', title: '', content: '', sort_order: items.length }); setShowForm(true); }}
              style={{ padding: '8px 14px', background: 'linear-gradient(135deg,#667eea,#764ba2)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
              + إضافة معلومة
            </button>
            {!embedded && <button onClick={onClose} style={{ padding: '8px 12px', background: '#1e293b', color: '#94a3b8', border: '1px solid #334155', borderRadius: '8px', cursor: 'pointer' }}>✕</button>}
          </div>
        </div>

        {/* Form */}
        {showForm && (
          <div style={{ background: '#1e293b', borderRadius: '12px', padding: '16px', marginBottom: '16px', border: '1px solid #334155' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
              <div>
                <label style={{ fontSize: '12px', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>الفئة</label>
                <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} style={INP}>
                  {CATS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>الترتيب</label>
                <input type="number" value={form.sort_order} onChange={e => setForm(p => ({ ...p, sort_order: Number(e.target.value) }))} style={INP} />
              </div>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ fontSize: '12px', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>العنوان / السؤال</label>
              <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="مثال: كورس الذكاء الاصطناعي المستوى الأول" style={INP} />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontSize: '12px', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>المحتوى التفصيلي</label>
              <textarea value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))}
                placeholder="اكتب كل المعلومات اللي المساعد يعرفها عن الموضوع ده..."
                rows={5} style={{ ...INP, resize: 'vertical', lineHeight: '1.6' }} />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={save} disabled={saving} style={{ padding: '8px 16px', background: 'linear-gradient(135deg,#667eea,#764ba2)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
                {saving ? '...' : editing ? '💾 تعديل' : '✅ حفظ'}
              </button>
              <button onClick={() => { setShowForm(false); setEditing(null); }} style={{ padding: '8px 16px', background: '#0f172a', color: '#94a3b8', border: '1px solid #334155', borderRadius: '8px', cursor: 'pointer', fontSize: '12px' }}>إلغاء</button>
            </div>
          </div>
        )}

        {/* Items */}
        {loading ? (
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: '30px' }}>جاري التحميل...</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '500px', overflowY: 'auto' }}>
            {items.length === 0 && (
              <div style={{ textAlign: 'center', color: '#64748b', padding: '30px', border: '1px dashed #334155', borderRadius: '12px' }}>
                لا توجد معلومات بعد — أضف أول معلومة للمساعد!
              </div>
            )}
            {items.map(item => (
              <div key={item.id} style={{ background: item.is_active ? '#1e293b' : '#0f172a', borderRadius: '10px', padding: '12px 14px', border: `1px solid ${item.is_active ? '#334155' : '#1e293b'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', opacity: item.is_active ? 1 : 0.5 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                    <span style={{ background: '#334155', color: '#a78bfa', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold' }}>{item.category}</span>
                    <span style={{ color: 'white', fontWeight: 'bold', fontSize: '13px' }}>{item.title}</span>
                  </div>
                  <p style={{ color: '#94a3b8', fontSize: '12px', margin: 0, lineHeight: '1.6', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {item.content}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '5px', flexShrink: 0 }}>
                  <button onClick={() => toggle(item)} style={{ padding: '5px 8px', background: item.is_active ? '#166534' : '#7f1d1d', color: item.is_active ? '#4ade80' : '#f87171', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}>
                    {item.is_active ? '✓' : '✗'}
                  </button>
                  <button onClick={() => openEdit(item)} style={{ padding: '5px 8px', background: '#1e3a5f', color: '#60a5fa', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}>✏️</button>
                  <button onClick={() => del(item.id)} style={{ padding: '5px 8px', background: '#7f1d1d', color: '#f87171', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}>🗑</button>
                </div>
              </div>
            ))}
          </div>
        )}
    </Wrapper>
  );
}

// ── ServiceReviewsManager (آراء باقة الأجنسي) ──
function ServiceReviewsManager({ service, reviews, onSave, onDelete, onApprove, onUploadImage }) {
  const [showForm, setShowForm] = React.useState(false);
  const [editing, setEditing]   = React.useState(null);
  const [form, setForm] = React.useState({ client_name: '', rating: 5, comment_ar: '', avatar_url: '' });
  const [uploading, setUploading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const openAdd  = () => { setEditing(null); setForm({ client_name: '', rating: 5, comment_ar: '', avatar_url: '' }); setShowForm(true); };
  const openEdit = (r) => { setEditing(r); setForm({ client_name: r.client_name || '', rating: r.rating || 5, comment_ar: r.comment_ar || '', avatar_url: r.avatar_url || '' }); setShowForm(true); };

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('الصورة أكبر من 5MB'); return; }
    setUploading(true);
    const url = await onUploadImage(file, 'review');
    if (url) setForm(f => ({ ...f, avatar_url: url }));
    else alert('فشل الرفع');
    setUploading(false);
  };

  const submit = async () => {
    if (!form.client_name.trim()) { alert('اكتب اسم العميل'); return; }
    setSaving(true);
    await onSave({
      service_id: service.id,
      client_name: form.client_name.trim(),
      rating: Number(form.rating) || 5,
      comment_ar: form.comment_ar || null,
      avatar_url: form.avatar_url || null,
      is_approved: true,
    }, editing);
    setSaving(false); setShowForm(false); setEditing(null);
  };

  return (
    <div style={{ background: '#faf5ff', borderRadius: '12px', padding: '14px', margin: '8px 0 14px', border: '1px solid #e9d5ff' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
        <h4 style={{ fontSize: '13px', fontWeight: 'bold', color: '#7c3aed', margin: 0 }}>💬 آراء العملاء على باقة "{service.name_ar}"</h4>
        <button onClick={openAdd} style={{ padding: '7px 13px', background: G2, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold', fontSize: '12px' }}>
          <Plus size={13} /> إضافة رأي
        </button>
      </div>

      {showForm && (
        <div style={{ background: 'white', borderRadius: '10px', padding: '14px', marginBottom: '12px', border: '1px solid #e5e7eb' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151', display: 'block', marginBottom: '4px' }}>اسم العميل</label>
              <input value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} placeholder="اسم العميل" style={INP} />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151', display: 'block', marginBottom: '4px' }}>التقييم</label>
              <select value={form.rating} onChange={e => setForm(f => ({ ...f, rating: e.target.value }))} style={INP}>
                {[5, 4, 3, 2, 1].map(n => <option key={n} value={n}>{'⭐'.repeat(n)} ({n})</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151', display: 'block', marginBottom: '4px' }}>التعليق</label>
            <textarea value={form.comment_ar} onChange={e => setForm(f => ({ ...f, comment_ar: e.target.value }))} placeholder="رأي العميل في الباقة" rows="2" style={{ ...INP, resize: 'vertical' }} />
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: uploading ? '#9CA3AF' : '#EEF2FF', color: uploading ? 'white' : '#667eea', borderRadius: '8px', padding: '9px 16px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
              {uploading ? <><Loader size={13} style={SS} /> رفع...</> : '📷 صورة العميل (اختياري)'}
              <input type="file" accept="image/*" onChange={e => handleUpload(e.target.files?.[0])} disabled={uploading} style={{ display: 'none' }} />
            </label>
            {form.avatar_url && <img src={form.avatar_url} alt="avatar" style={{ display: 'inline-block', marginRight: '10px', width: '42px', height: '42px', objectFit: 'cover', borderRadius: '50%', verticalAlign: 'middle' }} />}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={submit} disabled={saving} style={{ flex: 1, padding: '10px', background: saving ? '#9CA3AF' : G2, color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'جاري الحفظ...' : (editing ? 'حفظ التعديل' : 'إضافة')}
            </button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} style={{ padding: '10px 18px', background: '#E5E7EB', color: '#374151', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>إلغاء</button>
          </div>
        </div>
      )}

      {reviews.length === 0 ? (
        <p style={{ fontSize: '12px', color: '#9ca3af', textAlign: 'center', margin: '8px 0' }}>لا توجد آراء بعد لهذه الباقة</p>
      ) : (
        <div style={{ display: 'grid', gap: '8px' }}>
          {[...reviews].sort((a, b) => (a.is_approved === false ? -1 : 0) - (b.is_approved === false ? -1 : 0)).map(r => {
            const pending = r.is_approved === false;
            return (
            <div key={r.id} style={{ background: pending ? '#fffbeb' : 'white', borderRadius: '8px', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: '10px', border: pending ? '1.5px solid #fcd34d' : '1px solid #f3f4f6' }}>
              {r.avatar_url
                ? <img src={r.avatar_url} alt={r.client_name} style={{ width: '34px', height: '34px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                : <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: G2, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', flexShrink: 0 }}>{(r.client_name || '؟').charAt(0)}</div>}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#1f2937' }}>
                  {r.client_name} <span style={{ fontSize: '11px' }}>{'⭐'.repeat(r.rating || 5)}</span>
                  {pending && <span style={{ fontSize: '10px', background: '#f59e0b', color: 'white', padding: '1px 7px', borderRadius: '8px', marginInlineStart: '6px', fontWeight: 'bold' }}>تحت المراجعة</span>}
                </div>
                {r.comment_ar && <div style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.comment_ar}</div>}
              </div>
              {pending && onApprove && (
                <button onClick={() => onApprove(r.id)} style={{ padding: '5px 10px', background: '#dcfce7', color: '#16a34a', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', flexShrink: 0 }}>✓ موافقة</button>
              )}
              <button onClick={() => openEdit(r)} style={{ padding: '5px 8px', background: '#dbeafe', color: '#2563eb', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', flexShrink: 0 }}>✏️</button>
              <button onClick={() => onDelete(r.id)} style={{ padding: '5px 8px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', flexShrink: 0 }}>🗑</button>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── AgencyWorksManager (أعمالنا) ──
function AgencyWorksManager({ works, services = [], onSave, onDelete, onUploadImage }) {
  const [showForm, setShowForm] = React.useState(false);
  const [editing, setEditing]   = React.useState(null);
  const [form, setForm] = React.useState({ title_ar: '', description_ar: '', video_url: '', thumbnail_url: '', service_id: '' });
  const [uploading, setUploading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const openAdd  = () => { setEditing(null); setForm({ title_ar: '', description_ar: '', video_url: '', thumbnail_url: '', service_id: '' }); setShowForm(true); };
  const openEdit = (w) => { setEditing(w); setForm({ title_ar: w.title_ar || '', description_ar: w.description_ar || '', video_url: w.video_url || '', thumbnail_url: w.thumbnail_url || '', service_id: w.service_id || '' }); setShowForm(true); };

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('الصورة أكبر من 5MB'); return; }
    setUploading(true);
    const url = await onUploadImage(file, 'agencywork');
    if (url) setForm(f => ({ ...f, thumbnail_url: url }));
    else alert('فشل الرفع');
    setUploading(false);
  };

  const submit = async () => {
    if (!form.title_ar.trim()) { alert('اكتب عنوان العمل'); return; }
    if (!form.video_url.trim()) { alert('حط رابط الفيديو'); return; }
    setSaving(true);
    await onSave({
      title_ar: form.title_ar.trim(),
      description_ar: form.description_ar || null,
      video_url: form.video_url.trim(),
      thumbnail_url: form.thumbnail_url || null,
      service_id: form.service_id ? Number(form.service_id) : null,
      is_active: true,
    }, editing);
    setSaving(false); setShowForm(false); setEditing(null);
  };

  return (
    <div id="sec-agency-works" style={{ marginBottom: '28px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
        <h3 style={{ fontSize: '15px', fontWeight: 'bold', color: '#1F2937', margin: 0 }}>🎬 أعمالنا (فيديوهات الأعمال السابقة)</h3>
        <button onClick={openAdd} style={{ padding: '8px 14px', background: G2, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold', fontSize: '12px' }}>
          <Plus size={13} /> إضافة عمل
        </button>
      </div>

      {showForm && (
        <div style={{ background: '#F9FAFB', borderRadius: '12px', padding: '16px', marginBottom: '14px', border: '1px solid #E5E7EB' }}>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151', display: 'block', marginBottom: '4px' }}>عنوان العمل</label>
            <input value={form.title_ar} onChange={e => setForm(f => ({ ...f, title_ar: e.target.value }))} placeholder="مثلاً: فيديو إعلاني لمطعم" style={INP} />
          </div>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151', display: 'block', marginBottom: '4px' }}>وصف مختصر (اختياري)</label>
            <textarea value={form.description_ar} onChange={e => setForm(f => ({ ...f, description_ar: e.target.value }))} placeholder="وصف العمل" rows="2" style={{ ...INP, resize: 'vertical' }} />
          </div>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151', display: 'block', marginBottom: '4px' }}>رابط الفيديو (Vimeo / YouTube / Drive)</label>
            <input value={form.video_url} onChange={e => setForm(f => ({ ...f, video_url: e.target.value }))} placeholder="https://vimeo.com/..." style={INP} />
          </div>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#374151', display: 'block', marginBottom: '4px' }}>📦 العمل ده تابع لأنهي باقة؟ (عشان العميل يفتحها وهو بيتفرج)</label>
            <select value={form.service_id} onChange={e => setForm(f => ({ ...f, service_id: e.target.value }))} style={INP}>
              <option value="">— بدون ربط —</option>
              {services.map(s => <option key={s.id} value={s.id}>{s.icon || '💼'} {s.name_ar}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: uploading ? '#9CA3AF' : '#EEF2FF', color: uploading ? 'white' : '#667eea', borderRadius: '8px', padding: '9px 16px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
              {uploading ? <><Loader size={13} style={SS} /> رفع...</> : '🖼️ صورة مصغّرة (اختياري)'}
              <input type="file" accept="image/*" onChange={e => handleUpload(e.target.files?.[0])} disabled={uploading} style={{ display: 'none' }} />
            </label>
            {form.thumbnail_url && <img src={form.thumbnail_url} alt="thumb" style={{ display: 'inline-block', marginRight: '10px', width: '90px', height: '54px', objectFit: 'cover', borderRadius: '8px', verticalAlign: 'middle' }} />}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={submit} disabled={saving} style={{ flex: 1, padding: '10px', background: saving ? '#9CA3AF' : G2, color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'جاري الحفظ...' : (editing ? 'حفظ التعديل' : 'إضافة')}
            </button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} style={{ padding: '10px 18px', background: '#E5E7EB', color: '#374151', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer' }}>إلغاء</button>
          </div>
        </div>
      )}

      {works.length === 0 ? (
        <p style={{ fontSize: '13px', color: '#9ca3af', textAlign: 'center', padding: '20px', background: '#f9fafb', borderRadius: '12px' }}>لا توجد أعمال بعد — اضغط "إضافة عمل" لإضافة أول فيديو</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: '12px' }}>
          {works.map(w => (
            <div key={w.id} style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', border: '1px solid #f3f4f6', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
              <div style={{ height: '110px', background: w.thumbnail_url ? `url(${w.thumbnail_url}) center/cover` : G2 }} />
              <div style={{ padding: '10px 12px' }}>
                <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#1f2937', marginBottom: '6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{w.title_ar}</div>
                {w.service_id && (() => {
                  const svc = services.find(s => s.id === w.service_id);
                  return svc ? <div style={{ fontSize: '11px', color: '#7c3aed', background: '#f5f3ff', borderRadius: '6px', padding: '3px 8px', marginBottom: '6px', display: 'inline-block', fontWeight: 'bold' }}>📦 {svc.name_ar}</div> : null;
                })()}
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button onClick={() => openEdit(w)} style={{ flex: 1, padding: '6px', background: '#dbeafe', color: '#2563eb', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>✏️ تعديل</button>
                  <button onClick={() => onDelete(w.id)} style={{ padding: '6px 10px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>🗑</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
